/**
 * Hook: POST /api/t/{slug}/candidates/{id}/format-resume (Phase 2.7.1).
 *
 * Fire-once enqueue mutation used by `<ResumeDocument>` on Resume tab
 * mount when (text present AND resume_html absent AND
 * resume_formatting=false AND resume_formatting_failed_at IS NULL).
 *
 * Three terminal handler responses:
 *   202 {status:"queued"}              → invalidate candidate query so the
 *                                        polling hook picks up the flag flip.
 *   200 {status:"already_formatting"} → same (another caller already
 *                                       enqueued or worker mid-execute).
 *   409 {status:"already_formatted",   → apiClient throws ApiError(status=409);
 *        resume_html}                    we catch it and re-fetch — the
 *                                        candidate row carries the canonical
 *                                        resume_html via GET /candidates/{id}.
 *
 * Idempotency-Key is sent as defense-in-depth (optional per the
 * 2.7.1 spec; the state-guarded UPDATE owns the user-facing dedupe).
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";
import { generateRequestId } from "@/lib/requestId";

type FormatResumeStatus =
  | "queued"
  | "already_formatting"
  | "already_formatted";

interface FormatResumeMutationResult {
  status: FormatResumeStatus;
}

export function useFormatResumeMutation(): UseMutationResult<
  FormatResumeMutationResult,
  ApiError,
  { candidateId: string }
> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const queryClient = useQueryClient();

  return useMutation<FormatResumeMutationResult, ApiError, { candidateId: string }>({
    mutationFn: async ({ candidateId }) => {
      try {
        const r = await apiClient.post<{ status: FormatResumeStatus }>(
          `/api/t/${tenantSlug}/candidates/${candidateId}/format-resume`,
          {},
          { headers: { "Idempotency-Key": generateRequestId() } },
        );
        return { status: r.data.status };
      } catch (err) {
        // 409 already_formatted is a soft outcome — the candidate already
        // has resume_html and the FE just needs to re-fetch to render it.
        // Translate to the same shape the 202/200 paths use; the
        // onSuccess invalidate picks up the canonical HTML.
        if (err instanceof ApiError && err.status === 409) {
          return { status: "already_formatted" };
        }
        throw err;
      }
    },
    onSuccess: async (_data, { candidateId }) => {
      // The worker just flipped (or will momentarily flip) the candidate
      // row. Invalidate so the polling hook picks up resume_formatting=TRUE
      // and starts its refetchInterval.
      await queryClient.invalidateQueries({
        queryKey: ["candidate", tenantSlug, candidateId],
      });
    },
  });
}
